import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    Button,
    Stack,
    TextField,
    Paper,
    CircularProgress,
    Divider,
    Link as MuiLink,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Tooltip from '@mui/material/Tooltip';
import { apiGet, apiPost, apiBuildUrl } from '../../../../api/client';
import { usePermissions } from '../../../../hooks/usePermissions';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';

/**
 * Fetches the auth-gated PDF as a blob so the SPA's session cookies
 * are sent, then renders it inline via an Object URL. Beats sending
 * users to a new browser tab where they lose encounter context.
 *
 * Two modes:
 *  - Finalized: auto-loads the signed summary PDF on mount.
 *  - Draft: loads an on-demand, server-built DRAFT preview (?preview=1)
 *    when the clinician asks for one, so they can review the document
 *    before committing to finalize. `onFlush` lets us push any pending
 *    autosave edits first so the preview reflects the latest state.
 */
// Completed amendments, newest last. This is an INTERNAL view — the PDF
// itself says nothing about having been corrected — so this list is the only
// place staff can see that a report was reissued, and why.
// Names resolve from the physician roster the editor already loaded.
function AmendmentHistory({ amendments, physicians }) {
    if (!amendments.length) return null;
    const nameOf = (id) => physicians.find((p) => String(p._id) === String(id || ''))?.name || 'Clinician';
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
                Amendment history · internal only
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.75 }}>
                {amendments.map((a, i) => (
                    <Paper
                        key={a._id || i}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 1.5, borderColor: 'rgba(5,47,93,0.12)', bgcolor: 'rgba(5,47,93,0.02)' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY }}>
                            Amendment {i + 1} — {new Date(a.closedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {' · '}{nameOf(a.closedBy || a.openedBy)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>{a.reason}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Sections changed: {(a.changedFields || []).join(', ') || 'No clinical content changed'}
                        </Typography>
                    </Paper>
                ))}
            </Stack>
        </Box>
    );
}

export default function SummaryTab({ encounter, patient, onFlush, physicians = [] }) {
    // "Email visit reports" is its own grantable permission (enforced
    // server-side on /api/encounters/:id/email as well).
    // ':write' rather than canAccess() — emailReports and finalizeEncounters
    // are write-only capabilities, so the "<module>:read" that canAccess()
    // looks for is never granted and every check silently fails closed.
    const { can, canWrite } = usePermissions();
    const canEmail = can('emailReports:write');
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [recipient, setRecipient] = useState('');
    const [emailing, setEmailing] = useState(false);
    const [emailMsg, setEmailMsg] = useState(null);
    const [emailErr, setEmailErr] = useState(null);
    const fetchedFor = useRef(null);
    const iframeRef = useRef(null);

    const isFinalized = encounter?.status && encounter.status !== 'draft';
    // While an amendment is open the stored PDF is still the LAST SIGNED
    // version — the edits in the editor aren't in it yet. So this tab behaves
    // like the draft case (explicit preview only), rather than auto-loading a
    // document that contradicts what the clinician is looking at.
    const amendments = encounter?.amendments || [];
    const openAmendment = amendments.find((a) => !a.closedAt) || null;
    const closedAmendments = amendments.filter((a) => a.closedAt);
    const isAmending = Boolean(openAmendment);
    const pdfUrl = apiBuildUrl(`api/encounters/${encounter?._id}/pdf`);

    // Signature hint: finalized reports carry the finalizing clinician's
    // signature from their profile; without one the signature block is
    // omitted from the PDF entirely. Warn users who could finalize this
    // encounter. Mirrors the editor's finalize gate: the grantable
    // "finalizeEncounters" permission + "encounters" write access.
    const { role: rolePath } = useParams();
    const canSign = can('finalizeEncounters:write');
    const canFinalizeHere = canSign && canWrite('encounters');
    const [myProfile, setMyProfile] = useState(null);

    useEffect(() => {
        if (!canSign) return undefined;
        let cancelled = false;
        apiGet('api/profile')
            .then((res) => { if (!cancelled) setMyProfile(res?.data || null); })
            .catch(() => { /* hint is best-effort — never block the summary */ });
        return () => { cancelled = true; };
    }, [canSign]);

    // Profile stores the signature only when both image and PIN are set.
    const hasSignature = Boolean(myProfile?.signatureImage && myProfile?.signaturePin);
    const missingSignature = canSign && myProfile && !hasSignature;
    // On a finalized encounter the hint only makes sense for the clinician
    // whose name is on the report (reviewedBy = whoever finalized it).
    const iAmSigner = myProfile && encounter?.reviewedBy
        && String(encounter.reviewedBy) === String(myProfile._id);
    const profileLink = (
        <MuiLink component={RouterLink} to={`/${rolePath}/edit-profile`}>
            My Profile
        </MuiLink>
    );

    // Readable download name, e.g. "Visit Summary - Joyann Toss - 2 Jun 2026.pdf".
    // Mirrors summaryFilename() on the server, which sets the same name in
    // Content-Disposition for direct (non-blob) downloads.
    const downloadName = (() => {
        const name = patient ? `${patient.fName || ''} ${patient.lName || ''}`.trim() : '';
        const d = encounter?.visitDate ? new Date(encounter.visitDate) : null;
        const dateStr = d && !Number.isNaN(d.getTime())
            ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : '';
        const base = ['Visit Summary', name, dateStr].filter(Boolean).join(' - ');
        return `${base.replace(/[\\/:*?"<>|]/g, '')}.pdf`;
    })();

    // Fetch the PDF as a blob. `preview` flips to the on-the-fly DRAFT build.
    const loadPdf = useCallback(async (preview, { signal } = {}) => {
        if (!encounter?._id) return;
        setLoading(true);
        setPreviewError(null);
        try {
            // Push pending edits before a draft preview so it reflects the
            // latest state the clinician sees in the editor.
            if (preview && onFlush) {
                try { await onFlush(); } catch { /* preview proceeds with last-saved */ }
            }
            const url = preview ? `${pdfUrl}?preview=1` : pdfUrl;
            const res = await fetch(url, { credentials: 'include', signal });
            if (!res.ok) throw new Error(`PDF request failed (${res.status})`);
            const blob = await res.blob();
            if (signal?.aborted) return;
            const objUrl = URL.createObjectURL(blob);
            setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return objUrl; });
        } catch (err) {
            if (err.name === 'AbortError') return;
            setPreviewError(err.message || 'Could not load PDF.');
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [encounter?._id, pdfUrl, onFlush]);

    // Auto-load the finalized summary once per encounter.
    useEffect(() => {
        if (!isFinalized || isAmending || !encounter?._id) return undefined;
        if (fetchedFor.current === encounter._id) return undefined;
        fetchedFor.current = encounter._id;
        const ctrl = new AbortController();
        loadPdf(false, { signal: ctrl.signal });
        return () => ctrl.abort();
    }, [isFinalized, isAmending, encounter?._id, loadPdf]);

    // Clean up blob URL on unmount
    useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

    const reloadFinalized = () => { fetchedFor.current = null; setBlobUrl(null); };
    const loadDraftPreview = () => loadPdf(true);

    // Print the embedded PDF preview directly. The blob URL is same-origin
    // with the SPA, so iframe.contentWindow.print() opens the browser's
    // native print dialog targeting the PDF. Falls back to opening a new
    // tab if the iframe isn't accessible (Safari blocks cross-frame print
    // in some configurations).
    const printPreview = () => {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                return;
            } catch {
                /* fall through to new-tab fallback */
            }
        }
        if (blobUrl) {
            // No 'noopener' here: Chrome refuses to open blob: URLs into a
            // noopener window ("Not allowed to load local resource"). The
            // blob is same-origin content we generated ourselves, so giving
            // the new tab an opener reference is safe.
            const w = window.open(blobUrl, '_blank');
            if (w) w.focus();
        }
    };

    const sendEmail = async () => {
        setEmailMsg(null); setEmailErr(null); setEmailing(true);
        try {
            const target = recipient.trim() || patient?.email;
            await apiPost(`api/encounters/${encounter._id}/email`, { recipient: target });
            setEmailMsg(`Summary emailed to ${target}.`);
        } catch (err) {
            setEmailErr(err.message || 'Failed to send email.');
        } finally {
            setEmailing(false);
        }
    };

    // Shared PDF viewer panel (toolbar + iframe), used for both the
    // finalized summary and the draft preview.
    const previewPanel = (draft) => (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                borderColor: 'rgba(5,47,93,0.10)',
                overflow: 'hidden',
                bgcolor: 'rgba(5,47,93,0.02)',
                minHeight: 480,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    px: 1.5, py: 1,
                    borderBottom: '1px solid rgba(5,47,93,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    rowGap: 0.5,
                    flexWrap: 'wrap',
                    bgcolor: '#fff',
                }}
            >
                <Typography variant="caption" sx={{ color: draft ? '#9B2C2C' : 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>
                    {!draft
                        ? 'Encounter summary preview'
                        : isAmending
                            ? 'Amendment preview — not yet reissued'
                            : 'Draft preview — not finalized'}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }} />
                <Button
                    size="small"
                    onClick={draft ? loadDraftPreview : reloadFinalized}
                    startIcon={<RefreshRoundedIcon fontSize="small" />}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                    Refresh
                </Button>
                <Button
                    size="small"
                    onClick={printPreview}
                    disabled={!blobUrl}
                    startIcon={<PrintRoundedIcon fontSize="small" />}
                    sx={{ textTransform: 'none', color: NAVY, fontWeight: 600 }}
                >
                    Print
                </Button>
                <Button
                    size="small"
                    component="a"
                    href={blobUrl || (draft ? `${pdfUrl}?preview=1` : pdfUrl)}
                    download={downloadName}
                    startIcon={<DownloadRoundedIcon fontSize="small" />}
                    sx={{ textTransform: 'none', color: NAVY, fontWeight: 600 }}
                >
                    Download
                </Button>
            </Box>
            <Box sx={{ flex: 1, position: 'relative', minHeight: 480 }}>
                {loading && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                        <CircularProgress size={28} sx={{ color: NAVY }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Generating PDF…</Typography>
                    </Box>
                )}
                {previewError && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">{previewError}</Alert>
                    </Box>
                )}
                {blobUrl && !loading && !previewError && (
                    <Box
                        component="iframe"
                        ref={iframeRef}
                        title={draft ? 'Encounter draft preview' : 'Encounter summary'}
                        src={blobUrl}
                        sx={{ width: '100%', height: 640, border: 0, display: 'block', bgcolor: '#fff' }}
                    />
                )}
            </Box>
        </Paper>
    );

    if (!isFinalized || isAmending) {
        return (
            <Box>
                <SectionHeading
                    title="Patient summary"
                    hint={isAmending
                        ? 'Preview your corrections, then use Complete amendment at the top to reissue the signed report.'
                        : 'Preview the summary now to check it reads well, then use Finalize encounter at the top to generate the signed PDF.'}
                />
                {isAmending ? (
                    <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5, mb: 2 }}>
                        An <strong>amendment is open</strong> on this signed report. The preview below reflects your
                        edits and is <strong>not signed</strong> — the currently signed version is still the one on
                        the Documents tab. Use <strong>Complete amendment</strong> at the top to reissue it.
                    </Alert>
                ) : (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5, mb: 2 }}>
                        This encounter is still a <strong>draft</strong>. Preview the summary below to review it, then
                        use the <strong>Finalize encounter</strong> button at the top once documentation is complete.
                    </Alert>
                )}
                <AmendmentHistory amendments={closedAmendments} physicians={physicians} />
                {missingSignature && canFinalizeHere && (
                    <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
                        You don&apos;t have a signature saved in your profile, so the report will
                        not include a signature. Add a signature and signature PIN in {profileLink}{' '}
                        before finalizing to have it appear on the report.
                    </Alert>
                )}

                {!blobUrl && !loading ? (
                    <Button
                        onClick={loadDraftPreview}
                        variant="contained"
                        startIcon={<VisibilityOutlinedIcon />}
                        sx={{
                            bgcolor: NAVY,
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                        }}
                    >
                        {isAmending ? 'Preview amended summary' : 'Preview draft summary'}
                    </Button>
                ) : (
                    previewPanel(true)
                )}
            </Box>
        );
    }

    return (
        <Box>
            <SectionHeading
                title="Patient summary"
                hint="Review the finalized encounter PDF. Print, download, or email it to the patient or referring clinician."
            />

            <Alert
                severity="success"
                variant="outlined"
                sx={{ mb: 2, borderRadius: 1.5 }}
                icon={false}
            >
                <Typography variant="body2">
                    Finalized {encounter.finalizedAt && (
                        <strong>on {new Date(encounter.finalizedAt).toLocaleString()}</strong>
                    )}
                    {closedAmendments.length > 0 && (
                        <>
                            {' · '}amended {closedAmendments.length === 1 ? 'once' : `${closedAmendments.length} times`},
                            most recently{' '}
                            <strong>
                                {new Date(closedAmendments[closedAmendments.length - 1].closedAt).toLocaleString()}
                            </strong>
                        </>
                    )}.
                </Typography>
            </Alert>

            <AmendmentHistory amendments={closedAmendments} physicians={physicians} />

            {missingSignature && iAmSigner && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
                    This report has no signature because none is saved in your profile. Add a
                    signature and signature PIN in {profileLink}, then click{' '}
                    <strong>Refresh</strong> to regenerate the report with it.
                </Alert>
            )}

            {previewPanel(false)}

            <Divider sx={{ my: 3 }} />

            <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
                    Email summary
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1 }}>
                    <TextField
                        size="small"
                        label="Recipient email"
                        value={recipient}
                        onChange={(e) => setRecipient(e.target.value)}
                        disabled={!canEmail}
                        sx={{ flex: 1 }}
                        placeholder={patient?.email || 'patient@example.com'}
                        helperText={patient?.email ? `Leave blank to send to ${patient.email}` : 'Patient has no email on file — enter a recipient.'}
                    />
                    <Tooltip title={canEmail ? '' : 'You do not have permission to email reports.'} arrow>
                        <span>
                            <Button
                                onClick={sendEmail}
                                disabled={emailing || !canEmail}
                                variant="contained"
                                startIcon={<EmailRoundedIcon />}
                                sx={{
                                    bgcolor: NAVY,
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                                }}
                            >
                                {emailing ? 'Sending…' : 'Send email'}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
                {emailMsg && <Alert severity="success" sx={{ mt: 1.5 }}>{emailMsg}</Alert>}
                {emailErr && <Alert severity="error" sx={{ mt: 1.5 }}>{emailErr}</Alert>}
            </Box>
        </Box>
    );
}
